import * as Api from '@ViewModels';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useTextingStepSave } from '../../hooks/useTextingStepSave';
import { useTextingStepState } from '../../hooks/useTextingStepState';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { TextingStepForm } from '../../components/TextingStepForm';
import { TextingStepAdvancedOptions } from '../../components/TextingStepAdvancedOptions';
import type { AutomationStepNodeData } from '../../types/stepNodes';

interface TextingStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function TextingStepRenderer({ nodeId, data, readOnly: readOnlyProp }: TextingStepRendererProps) {
	const errorMessages = useErrorMessages();

	const { automationStep, automationTemplate, stepData } = data;
	const textingStep = automationStep as Api.ITextingAutomationStep;
	const template = automationTemplate;

	const trigger = template?.draft?.trigger || template?.published?.trigger;
	const schedule = textingStep?.schedule || stepData?.schedule;

	const {
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		setShowRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		canEdit,
		readonly,
	} = useTextingStepState({ textingStep, template, readOnlyProp });

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveSuccess: () => {
			// No-op: success is tracked by saveStatus in autoSave hook
		},
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save, onSaveStep, saveSenderOptions } = useTextingStepSave({
		textingStep,
		stepId: data.stepId,
		templateId: template?.id,
		skipStepContactFilter,
		autoSave,
	});

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
	};

	if (!textingStep) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
			/>
			<TextingStepForm
				step={textingStep}
				trigger={trigger}
				schedule={schedule}
				readonly={readonly}
				showRequiredFieldError={showRequiredFieldError}
				onSaveStep={onSaveStep}
				onClearError={() => setShowRequiredFieldError(false)}
			/>
			<TextingStepAdvancedOptions
				step={textingStep}
				stepId={data.stepId || nodeId}
				nodeId={nodeId}
				filter={skipStepContactFilter}
				canEdit={canEdit}
				isExpanded={advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				onSaveSenderOptions={saveSenderOptions}
				onFilterChange={handleFilterChange}
			/>
		</>
	);
}
